import {
  AttributeType,
  ProjectSettingCodeType,
  SystemOptionType
} from 'data/types';

export type GallerySortOption = 'price-asc' | 'price-desc' | 'score';

export type GalleryFilterState = {
  inStock: boolean;
  priceMin: number | null;
  priceMax: number | null;
  onSale: boolean;
};

export const defaultGalleryFilters: GalleryFilterState = {
  inStock: true,
  priceMin: null,
  priceMax: null,
  onSale: false
};

export const gallerySortLabels: Record<GallerySortOption, string> = {
  score: 'Best Score',
  'price-asc': 'Price: Low to High',
  'price-desc': 'Price: High to Low'
};

export const gallerySortOptions: { value: GallerySortOption; label: string }[] =
  (Object.entries(gallerySortLabels) as [GallerySortOption, string][]).map(
    ([value, label]) => ({ value, label })
  );

export type CategoryWeightLevel = 'Low' | 'Medium' | 'High';

export type WeightedCategoryCode =
  | 'comfortPerformance'
  | 'installationDifficulty'
  | 'noise'
  | 'value';

export type CategoryWeightsState = Record<
  WeightedCategoryCode,
  CategoryWeightLevel
>;

export const categoryWeightLevelToMultiplier: Record<
  CategoryWeightLevel,
  number
> = {
  Low: 0,
  Medium: 1.0,
  High: 3.0
};

export const categoryWeightsToMultipliers = (
  weights: CategoryWeightsState
): Record<WeightedCategoryCode, number> => ({
  comfortPerformance:
    categoryWeightLevelToMultiplier[weights.comfortPerformance],
  installationDifficulty:
    categoryWeightLevelToMultiplier[weights.installationDifficulty],
  noise: categoryWeightLevelToMultiplier[weights.noise],
  value: categoryWeightLevelToMultiplier[weights.value]
});

export const categoryWeightLabels: Record<WeightedCategoryCode, string> = {
  comfortPerformance: 'Comfort & Performance',
  installationDifficulty: 'Easy Install',
  noise: 'Quiet Operation',
  value: 'Lowest Cost'
};

export const orderedWeightedCategories: WeightedCategoryCode[] = [
  'comfortPerformance',
  'value',
  'installationDifficulty',
  'noise'
];

export const categoryToProjectSettingCode: Record<
  WeightedCategoryCode,
  ProjectSettingCodeType
> = {
  comfortPerformance: 'performance_preference',
  value: 'value_preference',
  installationDifficulty: 'install_ease_preference',
  noise: 'noise_preference'
};

export const customerPreferenceAttributeCodes: ProjectSettingCodeType[] =
  orderedWeightedCategories.map((c) => categoryToProjectSettingCode[c]);

const normalizeWeightLevel = (raw: unknown): CategoryWeightLevel => {
  if (typeof raw !== 'string' || raw.length === 0) return 'Medium';
  const lower = raw.toLowerCase();
  if (lower === 'low') return 'Low';
  if (lower === 'high') return 'High';
  return 'Medium';
};

export const categoryWeightLevelsFromProjectSettings = (
  projectSettings: AttributeType[]
): CategoryWeightsState => ({
  comfortPerformance: normalizeWeightLevel(
    projectSettings.find(
      (ps) => ps.code === categoryToProjectSettingCode.comfortPerformance
    )?.value
  ),
  installationDifficulty: normalizeWeightLevel(
    projectSettings.find(
      (ps) => ps.code === categoryToProjectSettingCode.installationDifficulty
    )?.value
  ),
  noise: normalizeWeightLevel(
    projectSettings.find((ps) => ps.code === categoryToProjectSettingCode.noise)
      ?.value
  ),
  value: normalizeWeightLevel(
    projectSettings.find((ps) => ps.code === categoryToProjectSettingCode.value)
      ?.value
  )
});

export const categoryWeightsFromProjectSettings = (
  projectSettings: AttributeType[]
): Record<WeightedCategoryCode, number> =>
  categoryWeightsToMultipliers(
    categoryWeightLevelsFromProjectSettings(projectSettings)
  );

export function sortGalleryOptions(
  options: SystemOptionType[],
  sort: GallerySortOption
): SystemOptionType[] {
  const sorted = [...options];

  switch (sort) {
    case 'price-asc':
      sorted.sort((a, b) => a.price - b.price);
      break;
    case 'price-desc':
      sorted.sort((a, b) => b.price - a.price);
      break;
    case 'score':
      sorted.sort((a, b) => (b.score?.total ?? 0) - (a.score?.total ?? 0));
      break;
    default:
      break;
  }
  return sorted;
}
